import type { MatchCandidate, Omonimo, ProspectMatchChoice } from "../../api";
import { Badge, Btn } from "../../ui";
import { formatScore } from "./prospect-review-utils";

const VALID_CF = /^[A-Z0-9]{16}$/i;

function birthPlace(o: Omonimo): string {
  const luogo = o.luogo?.trim();
  const prov = o.prov?.trim();
  if (luogo && prov) return `${luogo} (${prov})`;
  return luogo || prov || "luogo non indicato";
}

/** Come confermare questo omonimo: per CF, o per riga SISTER se il CF manca
 * (persone registrate al catasto prima del codice fiscale). */
export function matchChoiceFor(o: Omonimo): ProspectMatchChoice | null {
  const cf = o.cf?.trim() ?? "";
  if (VALID_CF.test(cf)) return { cf };
  if (!cf && o.id?.trim()) return { omonimoId: o.id.trim() };
  return null;
}

/** Omonimi SISTER in ordine di score. Una riga per persona: a colonne quando il
 * contenitore è largo, impilata nella colonna laterale (container query). */
export function OmonimiList({
  rows,
  matchedCf,
  confirmedOmonimoId,
  topCandidate,
  canConfirm,
  busy,
  onConfirm,
}: {
  rows: Array<{ omonimo: Omonimo; cand: MatchCandidate | null }>;
  matchedCf: string | null;
  confirmedOmonimoId: string | null;
  topCandidate: MatchCandidate | null;
  canConfirm: boolean;
  busy: boolean;
  onConfirm: (choice: ProspectMatchChoice, omonimo: Omonimo) => void;
}) {
  return (
    <div className={`omonimi${canConfirm ? " with-actions" : ""}`}>
      <div className="omonimi-head" aria-hidden="true">
        <span>Omonimo</span>
        <span>Nascita</span>
        <span>Compatibilità con il necrologio</span>
        {canConfirm && <span />}
      </div>
      {rows.map(({ omonimo: o, cand }, i) => {
        const cf = o.cf?.trim() ?? "";
        const isMatch = (!!matchedCf && cf.toUpperCase() === matchedCf.toUpperCase())
          || (!!confirmedOmonimoId && !cf && o.id === confirmedOmonimoId);
        const isTop = cand !== null && cand === topCandidate;
        const incompatible = cand?.yearOff === true;
        const tone = isMatch ? " is-match" : isTop ? " is-top" : incompatible ? " is-off" : "";
        const choice = matchChoiceFor(o);
        return (
          <div key={(o.id || o.value || String(i)) + i} className={`omonimo${tone}`}>
            <div className="omonimo-id">
              <strong>{[o.cognome, o.nome].filter(Boolean).join(" ") || "—"}</strong>
              <code title={cf ? undefined : "SISTER non riporta il codice fiscale: si conferma la riga dell'elenco"}>
                {cf || "senza codice fiscale"}
              </code>
            </div>
            <div className="omonimo-birth">
              <span className="mono">{o.data?.trim() || "data non indicata"}</span>
              <span>{birthPlace(o)}</span>
            </div>
            <div className="omonimo-score">
              {cand ? (
                <>
                  <div className="omonimo-score-line">
                    <span className="mono" title="Punteggio di compatibilità, non probabilità">{formatScore(cand.score)}</span>
                    {isTop && <Badge kind="accent" title="Candidato con lo score migliore, nettamente sopra gli altri">consigliato</Badge>}
                    {isMatch && <Badge kind="info">{cf ? "CF confermato" : "confermato"}</Badge>}
                  </div>
                  {cand.reasons.length > 0 && (
                    <ul>
                      {cand.reasons.map((r) => <li key={r}>{r}</li>)}
                    </ul>
                  )}
                </>
              ) : (
                <span className="muted">non valutato</span>
              )}
            </div>
            {canConfirm && (
              <div className="omonimo-action">
                {choice && (
                  <Btn
                    size="sm"
                    variant={isTop ? "primary" : ""}
                    disabled={busy || incompatible}
                    title={incompatible ? "Anno di nascita incompatibile con il necrologio" : undefined}
                    onClick={() => onConfirm(choice, o)}
                  >
                    Conferma
                  </Btn>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
